/**
 * Frontend service for the assessment-lifecycle Medplum Bot.
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';

export const ASSESSMENT_LIFECYCLE_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'assessment-lifecycle',
} as const;

export function getAssessmentLifecycleBotExecuteTarget(): string | { system: string; value: string } {
  const id = import.meta.env.VITE_MEDPLUM_ASSESSMENT_LIFECYCLE_BOT_ID?.trim();
  if (id) return id;
  return ASSESSMENT_LIFECYCLE_BOT_IDENTIFIER;
}

export interface CancelAssessmentBotInput {
  action: 'cancel-assessment';
  taskId: string;
  reasonCode?: string;
  reasonText?: string;
}

export interface StopScheduleBotInput {
  action: 'stop-schedule';
  taskId: string;
  reasonCode?: string;
  reasonText?: string;
}

export interface ActivateScheduleBotInput {
  action: 'activate-schedule';
  taskId: string;
}

export interface CancelAssessmentBotResult {
  taskId: string;
  taskStatus: 'cancelled';
  encounterId?: string;
  encounterStatus?: 'cancelled';
  chargeItemId?: string;
  chargeItemStatus?: 'not-billable';
}

export async function executeCancelAssessment(
  medplum: MedplumClient,
  input: Omit<CancelAssessmentBotInput, 'action'>,
): Promise<CancelAssessmentBotResult> {
  return medplum.executeBot(
    getAssessmentLifecycleBotExecuteTarget(),
    { action: 'cancel-assessment', ...input } satisfies CancelAssessmentBotInput,
    ContentType.JSON,
  ) as Promise<CancelAssessmentBotResult>;
}

export async function executeStopSchedule(
  medplum: MedplumClient,
  input: Omit<StopScheduleBotInput, 'action'>,
): Promise<unknown> {
  return medplum.executeBot(
    getAssessmentLifecycleBotExecuteTarget(),
    { action: 'stop-schedule', ...input } satisfies StopScheduleBotInput,
    ContentType.JSON,
  );
}

export async function executeActivateSchedule(
  medplum: MedplumClient,
  input: Omit<ActivateScheduleBotInput, 'action'>,
): Promise<unknown> {
  return medplum.executeBot(
    getAssessmentLifecycleBotExecuteTarget(),
    { action: 'activate-schedule', ...input } satisfies ActivateScheduleBotInput,
    ContentType.JSON,
  );
}
